/**
 * Data hooks for the Dashboard — the operator's queue of active to-dos
 * (ADR-0082, Slice C → F).
 *
 *   useDashboard(viewerEmail?)      — fetches GET /api/dashboard for the
 *                                      signed-in viewer, or for another
 *                                      operator when viewerEmail is set
 *                                      (Slice F's Mine/Peer toggle).
 *   useDashboardCount(viewerEmail?) — fetches GET /api/dashboard/count.
 *                                      No arg → signed-in viewer (drives
 *                                      the sidebar Mine badge). With an
 *                                      arg → that operator's count (drives
 *                                      the peer count inside the toggle).
 *   useOutsideRoster(enabled)       — fetches GET /api/dashboard/outside-
 *                                      roster, the system owner's list of
 *                                      tasks seated off the roster (#1024).
 *                                      The page enables it only for the
 *                                      owner; the route refuses anyone else.
 *
 * Two hooks instead of one because the badge polls on every tab focus + a
 * 30s interval and lives in the chrome (always mounted), while the pane is
 * only mounted while the operator is on `/dashboard`. The count payload is
 * a single integer; keeping it separate from the row payload means the
 * badge can refresh without pulling the rows back over the wire — and lets
 * each peer count in the viewer picker refresh on its own cadence.
 *
 * Query key: keyed by viewer (`"mine"` when undefined; the email otherwise)
 * so no two operators' caches collide. Switching segments returns instantly
 * from cache; data still revalidates in the background.
 *
 * Re-fetch posture matches the operations-summary hook: the watched count
 * polls every 30s (matters move on the scale of minutes, not seconds), focus
 * refetches via the global default. Counts being displayed but not watched
 * poll on the slow cadence — see `useDashboardCount`.
 */

import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DashboardCountResponseSchema,
  DashboardResponseSchema,
  OutsideRosterResponseSchema,
  type DashboardCountResponse,
  type DashboardResponse,
  type OutsideRosterResponse,
} from "@command-center/shared";
import { apiGet, apiPost } from "./api";

const COUNT_POLL_INTERVAL_MS = 30_000;
/** Cadence for a count that is shown but not being watched (#708). */
const COUNT_SLOW_POLL_INTERVAL_MS = 180_000;

function viewerKey(viewerEmail?: string): string {
  return viewerEmail && viewerEmail.trim() !== ""
    ? viewerEmail.trim().toLowerCase()
    : "mine";
}

function withQuery(
  path: string,
  viewerEmail?: string,
  includeSnoozed?: boolean,
): string {
  const params: string[] = [];
  if (viewerEmail && viewerEmail.trim() !== "") {
    params.push(`viewer=${encodeURIComponent(viewerEmail.trim())}`);
  }
  if (includeSnoozed === true) {
    params.push("includeSnoozed=1");
  }
  return params.length === 0 ? path : `${path}?${params.join("&")}`;
}

export function useDashboard(
  viewerEmail?: string,
  opts?: { includeSnoozed?: boolean },
) {
  const includeSnoozed = opts?.includeSnoozed === true;
  return useQuery<DashboardResponse, Error>({
    // Snoozed-included payload keys separately from the default so toggling
    // doesn't pollute the cache that the sidebar count consumes.
    queryKey: [
      "dashboard",
      viewerKey(viewerEmail),
      includeSnoozed ? "with-snoozed" : "default",
    ] as const,
    queryFn: async () => {
      const raw = await apiGet<unknown>(
        withQuery("/api/dashboard", viewerEmail, includeSnoozed),
      );
      const parsed = DashboardResponseSchema.safeParse(raw);
      if (!parsed.success) {
        throw new Error(
          `/api/dashboard failed schema validation: ${parsed.error.message}`,
        );
      }
      return parsed.data;
    },
  });
}

/**
 * The system owner's outside-roster list (#1024). Keyed under ["dashboard"]
 * so every task write that invalidates the Dashboard (useTaskAction,
 * useAddMessage) refetches it too — an acted-on row leaves the band on the
 * next read. `enabled` is the page's owner check: no one else fetches it.
 */
export function useOutsideRoster(enabled: boolean) {
  return useQuery<OutsideRosterResponse, Error>({
    queryKey: ["dashboard", "outside-roster"] as const,
    enabled,
    queryFn: async () => {
      const raw = await apiGet<unknown>("/api/dashboard/outside-roster");
      const parsed = OutsideRosterResponseSchema.safeParse(raw);
      if (!parsed.success) {
        throw new Error(
          `/api/dashboard/outside-roster failed schema validation: ${parsed.error.message}`,
        );
      }
      return parsed.data;
    },
  });
}

/**
 * `watched` (default true) picks the cadence: 30s for a count the operator is
 * actually reading, 3 min for one that is merely on screen.
 *
 * Why the knob exists (#708): the viewer control renders one segment per peer,
 * and an unconditional 30s poll in each would grow the request rate linearly with
 * the roster — every open tab, forever, for peers nobody is looking at.
 *
 * Note what this is NOT: turning the timer off entirely. That was the first
 * fix and it was worse. Focus refetch does not fire for an operator who keeps
 * the dashboard focused all day, so an unwatched segment would freeze at its
 * mount value — a peer's badge reading 3 while their queue is really 11, and
 * work routed off the stale number. A count that misleads defeats the reason
 * the control shows counts at all. Slow beats frozen; both beat the linear
 * growth.
 *
 * `watched` flipping false→true also forces one immediate refetch. Changing
 * `refetchInterval` alone only restarts the timer, so without this a segment
 * clicked at t≈175s of its slow cycle would show a count fetched ~3 min ago
 * while the row list beside it refetched instantly on the new query key — the
 * header and the visible rows disagreeing for up to 30s. Narrower than the
 * frozen-badge case above, same failure.
 */
export function useDashboardCount(
  viewerEmail?: string,
  { watched = true }: { watched?: boolean } = {},
) {
  const query = useQuery<DashboardCountResponse, Error>({
    queryKey: ["dashboard-count", viewerKey(viewerEmail)] as const,
    queryFn: async () => {
      const raw = await apiGet<unknown>(
        withQuery("/api/dashboard/count", viewerEmail),
      );
      const parsed = DashboardCountResponseSchema.safeParse(raw);
      if (!parsed.success) {
        throw new Error(
          `/api/dashboard/count failed schema validation: ${parsed.error.message}`,
        );
      }
      return parsed.data;
    },
    refetchInterval: watched
      ? COUNT_POLL_INTERVAL_MS
      : COUNT_SLOW_POLL_INTERVAL_MS,
  });

  const { refetch } = query;
  const wasWatched = useRef(watched);
  useEffect(() => {
    if (watched && !wasWatched.current) void refetch();
    wasWatched.current = watched;
  }, [watched, refetch]);

  return query;
}

/**
 * Slice K (ADR-0099) seen-state mutations. Both write for the AUTHENTICATED
 * operator server-side (never the `?viewer` override), so they are only
 * called from the Mine view — the page suppresses auto-mark and the
 * "Mark all seen" button on the Peer tab. Invalidate-only, matching the
 * use-surface-tasks discipline; no optimistic updates — the expand auto-mark
 * is best-effort (callers ignore errors), Mark-all-seen refetches on success.
 */
export function useMarkTaskSeen() {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, { id: string }>({
    mutationFn: ({ id }) =>
      apiPost<unknown>(
        `/api/dashboard/tasks/${encodeURIComponent(id)}/mark-seen`,
        {},
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard-count"] });
    },
  });
}

export function useMarkAllSeen() {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, void>({
    mutationFn: () => apiPost<unknown>("/api/dashboard/mark-all-seen", {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard-count"] });
    },
  });
}
